import { useEffect, useState } from "react";
import { majorUnits } from "../../src/billing/minor.ts";
import { api, type MyBilling, type OwnedAddon, type OwnedPass, type PartnerPanel, type Receipt } from "./api.ts";
import { useLoad } from "./hooks.ts";
import { useAgain } from "./offline.ts";
import { buyFrequentFlyer, buyPass, renewal } from "./paying.ts";
import { HaveACode, REDEEMED_EVENT } from "./Redeem.tsx";
import { RefundAction, refundLine } from "./Refund.tsx";
import { navigate, tripPath } from "./router.ts";
import { SettingsGroup } from "./SettingsGroup.tsx";
import { ButtonLink, CopyButton } from "./ui.tsx";

// Billing on the profile (features/paying.md, spec §8.4): what I'm on and its state, every Trip Pass and
// spots I bought with what became of them, receipts (Stripe's own), refunds' eligibility shown up front,
// and where to write. Buying is a link to Stripe's Checkout, back here after.

const DAY = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric" });
const day = (iso: string) => DAY.format(new Date(iso));
const money = (minor: number, currency: string) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: currency.toUpperCase() }).format(
    majorUnits(minor, currency),
  );

const WHAT: Record<Receipt["kind"], string> = {
  trip_pass: "Trip Pass",
  seat_addon: "More spots",
  yearly: "Frequent Flyer",
  yearly_renewal: "Frequent Flyer, renewed",
  lifetime: "Lifetime",
};

export function BillingSection() {
  // Asked for, or withdrawn, a refund, or a code redeemed (Redeem.tsx): loaded again.
  const [changed, setChanged] = useState(0);
  useEffect(() => {
    const again = () => setChanged((n) => n + 1);
    addEventListener(REDEEMED_EVENT, again);
    return () => removeEventListener(REDEEMED_EVENT, again);
  }, []);
  const billing = useLoad(api.billing, [useAgain(), changed]);
  return (
    <SettingsGroup id="billing-title" title="Billing" className="tm-billing">
      {billing.state === "failed" && (
        <div className="tm-settings-part">
          <p className="callout tm-muted">{billing.message}</p>
        </div>
      )}
      {billing.state === "ready" && <Billing billing={billing.data} onChanged={() => setChanged((n) => n + 1)} />}
    </SettingsGroup>
  );
}

function Billing({ billing, onChanged }: { billing: MyBilling; onChanged: () => void }) {
  const { plan, summary, passes, addons, receipts } = billing;
  const planRefund = plan ? refundLine(plan.refund, plan.refund_request) : null;
  const saved = passes.filter((p) => p.state === "saved").length;
  return (
    <>
      {billing.restrictions.length > 0 && (
        <div className="tm-settings-part">
          <Restrictions billing={billing} />
        </div>
      )}
      <div className="tm-settings-part">
        <div className="tm-billing-plan">
          <p className="body-strong">{plan ? plan.name : "No plan"}</p>
          <PlanState billing={billing} />
          <p className="callout tm-muted">{planLine(billing)}</p>
          {plan && planRefund && <p className="callout tm-muted">{planRefund}</p>}
          {plan && (
            <RefundAction
              target={{ type: "entitlement", id: plan.id }}
              what={plan.name}
              offer={plan.refund}
              request={plan.refund_request}
              takesBack={`${plan.name} ends now${plan.subscription ? ", and nothing more is charged" : ""}.`}
              className="tm-edge-start"
              onChanged={onChanged}
            />
          )}
          {saved > 0 && (
            <p className="callout tm-muted">
              {saved === 1 ? "1 Trip Pass saved" : `${saved} Trip Passes saved`} for later, if your plan ends.
            </p>
          )}
        </div>
        {(plan?.subscription || (summary.plan === "none" && summary.buying)) && (
          <div className="tm-billing-actions">
            {plan?.subscription && (
              <ButtonLink href="/billing/portal" variant="secondary" size="sm">
                Manage subscription
              </ButtonLink>
            )}
            {summary.plan === "none" && summary.buying && (
              <>
                <ButtonLink href={buyPass("/profile")} variant="secondary" size="sm">
                  Get a Trip Pass · {summary.prices.trip_pass}
                </ButtonLink>
                <HaveACode />
                <ButtonLink href={buyFrequentFlyer("/profile")} variant="secondary" size="sm">
                  {/* The name and the price each kept whole: on a narrow phone the button wraps between them. */}
                  <span className="tm-nowrap">Get Frequent Flyer ·</span>{" "}
                  <span className="tm-nowrap">{summary.prices.frequent_flyer} a year</span>
                </ButtonLink>
                <p className="callout tm-muted tm-billing-renews">{renewal(summary.prices.frequent_flyer)}</p>
              </>
            )}
          </div>
        )}
      </div>
      {billing.partner && <Partner partner={billing.partner} />}
      {passes.length > 0 && (
        <div className="tm-settings-part tm-billing-group">
          <h3 className="tm-settings-label">Trip Passes</h3>
          <ul className="tm-billing-list">
            {passes.map((p) => (
              <PassRow key={p.id} pass={p} onChanged={onChanged} />
            ))}
          </ul>
        </div>
      )}
      {addons.length > 0 && (
        <div className="tm-settings-part tm-billing-group">
          <h3 className="tm-settings-label">Spots</h3>
          <ul className="tm-billing-list">
            {addons.map((a) => (
              <AddonRow key={a.id} addon={a} onChanged={onChanged} />
            ))}
          </ul>
        </div>
      )}
      {receipts.length > 0 && (
        <div className="tm-settings-part tm-billing-group">
          <h3 className="tm-settings-label">Receipts</h3>
          <ul className="tm-billing-list">
            {receipts.map((r) => (
              <li key={r.id} className="tm-billing-row">
                <span className="tm-billing-text">
                  <span className="callout tm-billing-name">
                    {WHAT[r.kind]} · {money(r.amount, r.currency)}
                  </span>
                  <span className="caption tm-muted">
                    {day(r.paid_at)}
                    {r.refunded > 0
                      ? ` · ${r.refunded >= r.amount ? "refunded" : `${money(r.refunded, r.currency)} refunded`}`
                      : r.status === "disputed" || r.status === "dispute_lost"
                        ? " · disputed"
                        : ""}
                  </span>
                </span>
                <a className="tm-billing-link callout" href={r.url} target="_blank" rel="noopener">
                  Receipt
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="tm-settings-part">
        <p className="caption tm-muted">
          Tripmo is sold through Link, Stripe's reseller: your card statement says LINK.COM* TRIPMO.ME, and receipts
          come from Link. A refund is for something you haven't used, within 14 days of paying. Questions about a
          payment? Write to{" "}
          <a className="tm-billing-mail" href={`mailto:${billing.support_email}`}>
            {billing.support_email}
          </a>
          .
        </p>
      </div>
    </>
  );
}

// What's paused on the account (abuse review's restrictions), each with until when, and where to write: a
// person at Tripmo looks at it when asked (features/privacy-and-data.md, "Fair use and abuse").
function Restrictions({ billing }: { billing: MyBilling }) {
  return (
    <div className="tm-billing-hold" role="status">
      {billing.restrictions.map((r) => (
        <p key={`${r.kind}-${r.since}`} className="callout body-strong">
          {r.words}
          {r.until && <span className="tm-muted"> Until {day(r.until)}.</span>}
        </p>
      ))}
      <p className="callout tm-muted">
        Everything else on your account keeps working. If you think this is a mistake, write to{" "}
        <a className="tm-billing-mail" href={`mailto:${billing.support_email}`}>
          {billing.support_email}
        </a>{" "}
        and a person at Tripmo will look at it.
      </p>
    </div>
  );
}

// Where the plan stands: a subscription renewing or cancelled (Stripe's portal), its card failing while
// Stripe tries again, or paid through a date; nothing for a plan with no end.
function PlanState({ billing: { plan } }: { billing: MyBilling }) {
  if (!plan) return null;
  if (plan.status === "past_due")
    return (
      <>
        <p className="callout tm-error">Payment failed: update your card.</p>
        <p className="callout tm-muted">
          Stripe tries the card again for a while; until it gives up, your plan carries on.
        </p>
      </>
    );
  if (!plan.ends_at) return null;
  const when = day(plan.ends_at);
  const line =
    plan.renewal === "renews"
      ? `Renews ${when}`
      : plan.renewal === "cancels"
        ? `Cancels ${when}`
        : `Paid through ${when}`;
  return <p className="callout">{line}</p>;
}

// What the plan means, in a line.
function planLine({ plan, summary }: MyBilling): string {
  if (!plan)
    return `Each trip needs a Trip Pass (${summary.prices.trip_pass}, up to 5 people, you included), or Frequent Flyer (${summary.prices.frequent_flyer} a year) covers every trip. People join your trips free.`;
  if (plan.renewal === "cancels")
    return "It won't renew. Until then trips are included; the trips you start stay yours to edit after it ends.";
  return "Trips are included, with unlimited spots on the trips you own.";
}

function PassRow({ pass, onChanged }: { pass: OwnedPass; onChanged: () => void }) {
  const state =
    pass.state === "waiting"
      ? "waiting on Your trips"
      : pass.state === "saved"
        ? "saved for later"
        : pass.state === "used"
          ? null
          : pass.state === "refunded"
            ? "refunded"
            : "on hold: its payment is disputed";
  const refund = refundLine(pass.refund, pass.refund_request, pass.state === "refunded");
  return (
    <li className="tm-billing-row">
      <span className="tm-billing-text">
        {/* A free one, from a code or the sign-up gift: nothing was paid, so there's no receipt and no refund
            (features/paying.md, "Free Trip Passes from codes", "A free first trip for every sign-up"). */}
        <span className="callout tm-billing-name">
          {pass.gift
            ? "Free Trip Pass, a gift for signing up"
            : pass.free
              ? "Free Trip Pass, from a code"
              : "Trip Pass"}
        </span>
        <span className="caption tm-muted">
          {pass.gift ? "Given" : pass.free ? "Redeemed" : "Bought"} {day(pass.bought_at)} ·{" "}
          {state ?? <UsedOn pass={pass} />}
        </span>
        {refund && <span className="caption tm-muted">{refund}</span>}
      </span>
      <RefundAction
        target={{ type: "trip_pass", id: pass.id }}
        what="Trip Pass"
        offer={pass.refund}
        request={pass.refund_request}
        takesBack={
          pass.trip
            ? `${pass.trip.title} is deleted with it; you can restore it for 30 days, to read.`
            : "The pass goes from Your trips."
        }
        onChanged={onChanged}
      />
    </li>
  );
}

// "used on Kyoto", opening it; a trip deleted since says so.
function UsedOn({ pass }: { pass: OwnedPass }) {
  const trip = pass.trip;
  if (!trip) return <>used on a trip since deleted</>;
  if (trip.deleted) return <>used on {trip.title}, deleted</>;
  return (
    <>
      used on{" "}
      <a
        className="tm-billing-trip"
        href={tripPath(trip.id)}
        onClick={(e) => {
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
          e.preventDefault();
          navigate(tripPath(trip.id));
        }}
      >
        {trip.title}
      </a>
    </>
  );
}

function AddonRow({ addon, onChanged }: { addon: OwnedAddon; onChanged: () => void }) {
  const spots = `+${addon.blocks * 5} spots`;
  const state = addon.status === "refunded" ? "refunded" : addon.status === "disputed" ? "on hold" : null;
  const refund = refundLine(addon.refund, addon.refund_request, addon.status === "refunded");
  return (
    <li className="tm-billing-row">
      <span className="tm-billing-text">
        <span className="callout tm-billing-name">
          {addon.trip ? `${spots} on ${addon.trip.title}` : `${spots} on a trip since deleted`}
        </span>
        <span className="caption tm-muted">
          Bought {day(addon.bought_at)}
          {state ? ` · ${state}` : addon.used ? " · in use" : " · not needed yet"}
        </span>
        {refund && <span className="caption tm-muted">{refund}</span>}
      </span>
      <RefundAction
        target={{ type: "seat_addon", id: addon.id }}
        what={addon.trip ? `${spots} on ${addon.trip.title}` : spots}
        offer={addon.refund}
        request={addon.refund_request}
        takesBack={`The ${addon.blocks * 5} spots go from ${addon.trip?.title ?? "the trip"}.`}
        onChanged={onChanged}
      />
    </li>
  );
}

const MONTH = new Intl.DateTimeFormat("en-GB", { month: "long", year: "numeric", timeZone: "UTC" });
// "October 2026", from "2026-10".
const month = (period: string) => MONTH.format(new Date(`${period}-01T00:00:00Z`));

// A partner's own corner (spec §8.4): their code and link to share, what the people they sent have paid,
// their commission held, to be paid and paid, and their payouts.
function Partner({ partner: p }: { partner: PartnerPanel }) {
  const usd = (cents: number) => money(cents, p.currency);
  const earned = [
    p.held > 0 && `${usd(p.held)} held for 30 days`,
    p.payable > 0 && `${usd(p.payable)} to be paid`,
    p.paid > 0 && `${usd(p.paid)} paid`,
  ].filter(Boolean);
  const shown = p.share_link.replace(/^https?:\/\//, "");
  return (
    <div className="tm-settings-part tm-billing-group">
      <h3 className="tm-settings-label">Your partnership</h3>
      <ul className="tm-billing-list">
        {p.status === "ended" ? (
          <li className="tm-billing-row">
            <span className="tm-billing-text">
              <span className="callout tm-billing-name">Your partnership has ended</span>
              <span className="caption tm-muted">What you earned until then is still paid.</span>
            </span>
          </li>
        ) : (
          <>
            {p.code && (
              <li className="tm-billing-row">
                <span className="tm-billing-text">
                  <span className="callout tm-billing-name">
                    Your code <code className="tm-partner-code">{p.code}</code>
                  </span>
                  <span className="caption tm-muted">{p.discount_percent}% off for the people who use it</span>
                </span>
                <CopyButton text={p.code} label="Copy code" className="tm-billing-copy" />
              </li>
            )}
            <li className="tm-billing-row">
              <span className="tm-billing-text">
                <span className="callout tm-billing-name">Your link</span>
                <span className="caption tm-muted">{shown}</span>
              </span>
              <CopyButton text={p.share_link} label="Copy link" className="tm-billing-copy" />
            </li>
          </>
        )}
        <li className="tm-billing-row">
          <span className="tm-billing-text">
            <span className="callout tm-billing-name">
              {p.sales === 0 ? "No sales yet" : p.sales === 1 ? "1 sale" : `${p.sales} sales`}
              {p.sales > 0 && ` · ${usd(p.sales_amount)}`}
            </span>
            <span className="caption tm-muted">
              {earned.length > 0
                ? `You earned ${earned.join(" · ")}`
                : "A sale is a new customer's first payment, made with your code or link."}
            </span>
          </span>
        </li>
        {p.payouts.map((o) => (
          <li key={o.period} className="tm-billing-row">
            <span className="tm-billing-text">
              <span className="callout tm-billing-name">
                Payout for {month(o.period)} · {usd(o.net)}
              </span>
              <span className="caption tm-muted">
                {o.status === "paid" && o.paid_at ? `Paid ${day(o.paid_at)}` : "On its way"}
              </span>
            </span>
          </li>
        ))}
      </ul>
      <p className="caption tm-muted">
        Your share is {p.commission_percent}% of each new customer's first payment, after any discount. It's held 30
        days, then paid each month by PayNow, Wise or PayPal once it reaches {usd(p.minimum)}; less carries forward.{" "}
        <a className="tm-billing-mail" href="https://tripmo.me/terms#partners" target="_blank" rel="noopener">
          Partner terms
        </a>
      </p>
    </div>
  );
}
